<template>
  <div class="space-y-4">
    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        @click="openFilePicker"
        :disabled="disabled"
        class="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50">
        Upload Image
      </button>
      <button
        type="button"
        @click="toggleGallery"
        :disabled="disabled"
        class="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50">
        {{ showGallery ? 'Hide Gallery' : 'Select Existing' }}
      </button>
      <button
        v-if="selectedFile"
        type="button"
        @click="clearSelection"
        :disabled="disabled"
        class="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
        Discard Change
      </button>
      <button
        v-if="currentImageUrl && !selectedFile"
        type="button"
        @click="$emit('remove')"
        :disabled="disabled"
        class="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
        Remove Image
      </button>
    </div>

    <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileSelect" />

    <div
      class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center transition-colors"
      :class="[
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-blue-500',
        dragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : '',
      ]"
      @dragover.prevent="handleDragOver"
      @dragleave="dragOver = false"
      @drop.prevent="handleFileDrop"
      @click="openFilePicker">
      <div v-if="!displayImageUrl">
        <span class="material-symbols-outlined text-3xl text-gray-400 mb-1 block">upload_file</span>
        <div class="text-sm text-gray-600 dark:text-gray-400">Click or drag an image here</div>
        <div class="text-xs text-gray-500 mt-1">PNG, JPG, GIF, WebP (max 50MB)</div>
      </div>
      <div v-else>
        <img :src="displayImageUrl" class="max-h-48 mx-auto mb-3 rounded object-contain" alt="Floor plan preview" />
        <div class="text-sm text-gray-700 dark:text-gray-300 font-medium break-all">
          {{ selectedFile ? selectedFile.name : currentImageName || 'Current floor plan' }}
        </div>
        <div class="text-xs text-gray-500 mt-1">
          {{
            selectedFile
              ? `New image selected — click ${confirmActionLabel} to apply`
              : 'Current image — upload or select an existing floor plan to replace it'
          }}
        </div>
      </div>
    </div>

    <div v-if="error" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <div class="text-sm text-red-600 dark:text-red-400">{{ error }}</div>
    </div>

    <div v-if="showGallery" class="space-y-3 pt-2 border-t dark:border-gray-700">
      <div class="flex items-center justify-between gap-3">
        <div>
          <div class="text-sm font-medium text-gray-700 dark:text-gray-300">Existing Floor Plans</div>
          <div class="text-xs text-gray-500 dark:text-gray-400">
            Selecting an existing image makes it the pending floor plan.
          </div>
        </div>
        <button
          type="button"
          @click="loadGalleryImages"
          :disabled="loadingGallery || disabled"
          class="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50">
          Refresh
        </button>
      </div>

      <div v-if="galleryError" class="text-xs text-red-500">{{ galleryError }}</div>

      <div v-if="loadingGallery" class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div v-for="n in 6" :key="n" class="aspect-[4/3] rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
      </div>

      <div
        v-else-if="galleryImages.length === 0"
        class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
        No existing dashboard floor plans found in the gallery.
      </div>

      <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto pr-1">
        <button
          v-for="image in galleryImages"
          :key="image.id"
          type="button"
          @click="selectGalleryImage(image)"
          :disabled="disabled"
          class="group rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden text-left hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all disabled:opacity-50">
          <div class="aspect-[4/3] bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <img
              :src="image.fullUrl"
              :alt="image.fileName || image.entityName"
              class="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
          </div>
          <div class="p-2.5 space-y-1">
            <div class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
              {{ image.entityName || 'Dashboard' }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ image.fileName || 'Floor plan' }}</div>
            <div class="text-xs text-gray-400 dark:text-gray-500">{{ image.width }} × {{ image.height }}</div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useImageAssetsStore } from '@/stores/imageAssets';
import { resolveImageUrl } from '@/services/mediaLibraryUrls';
import { apiFetch } from '@/services/api';

interface GalleryImage {
  id: string;
  entityName: string;
  fileName: string;
  fullUrl: string;
  width: number;
  height: number;
}

/** One row of the `/images` gallery response (the raw image record). */
interface GalleryImageRow {
  id?: string | number;
  entityName?: string;
  fileName?: string;
  imageUrl?: string;
  filePath?: string;
  width?: number | string;
  height?: number | string;
}

interface Props {
  selectedFile: File | null;
  previewUrl: string;
  currentImageUrl?: string;
  currentImageName?: string;
  confirmActionLabel?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  currentImageUrl: '',
  currentImageName: '',
  confirmActionLabel: 'Save',
  disabled: false,
});

const emit = defineEmits<{
  (e: 'update:selectedFile', value: File | null): void;
  (e: 'update:previewUrl', value: string): void;
  (e: 'remove'): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const imageAssetsStore = useImageAssetsStore();
const dragOver = ref(false);
const showGallery = ref(false);
const galleryImages = ref<GalleryImage[]>([]);
const loadingGallery = ref(false);
const galleryLoaded = ref(false);
const galleryError = ref('');
const error = ref('');

const displayImageUrl = computed(() => props.previewUrl || props.currentImageUrl || '');

function openFilePicker() {
  if (props.disabled) return;
  fileInput.value?.click();
}

function handleDragOver() {
  if (props.disabled) return;
  dragOver.value = true;
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    void processFile(file);
  }
  input.value = '';
}

function handleFileDrop(event: DragEvent) {
  dragOver.value = false;
  if (props.disabled) return;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    void processFile(file);
  }
}

function clearSelection() {
  error.value = '';
  emit('update:selectedFile', null);
  emit('update:previewUrl', '');
}

async function processFile(file: File) {
  error.value = '';

  if (!file.type.startsWith('image/')) {
    error.value = 'Please select an image file';
    return;
  }

  if (file.size > 50 * 1024 * 1024) {
    error.value = 'File size must be less than 50MB';
    return;
  }

  selectFile(file);
}

function selectFile(file: File) {
  error.value = '';
  emit('update:selectedFile', file);
  emit('update:previewUrl', URL.createObjectURL(file));
}

async function toggleGallery() {
  showGallery.value = !showGallery.value;
  if (showGallery.value && !galleryLoaded.value) {
    await loadGalleryImages();
  }
}

async function loadGalleryImages() {
  galleryError.value = '';
  loadingGallery.value = true;

  try {
    const response = await apiFetch<{ images?: GalleryImageRow[] }>('/images?entityType=dashboard&imageType=layout');
    galleryImages.value = (response.images || [])
      .map((img) => ({
        id: String(img.id),
        entityName: img.entityName || 'Dashboard',
        fileName: img.fileName || '',
        fullUrl: img.imageUrl || img.filePath || '',
        width: Number(img.width) || 0,
        height: Number(img.height) || 0,
      }))
      .filter((img: GalleryImage) => Boolean(img.fullUrl));
    galleryLoaded.value = true;
  } catch (__caught: unknown) {
    const err = __caught as Error;
    galleryError.value = err.message || 'Failed to load existing floor plans';
    galleryImages.value = [];
  } finally {
    loadingGallery.value = false;
  }
}

async function selectGalleryImage(image: GalleryImage) {
  error.value = '';

  try {
    // The row's bytes come through the image cache (works on every transport);
    // the preview URL is only a fallback for a row the cache cannot answer.
    const blob =
      (await imageAssetsStore.fetchImageBlob(image.id, 'original')) ||
      (await fetch(resolveImageUrl(image.fullUrl) || image.fullUrl).then((response) => response.blob()));
    if (!blob) {
      error.value = 'Could not load that image';
      return;
    }
    const name = image.fileName || `${image.entityName || 'floor-plan'}.${extensionFor(blob.type)}`;
    selectFile(new File([blob], name, { type: blob.type || 'image/png' }));
    showGallery.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Could not load that image';
  }
}

function extensionFor(mimeType: string): string {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/gif') return 'gif';
  return 'jpg';
}
</script>
